import { apiFetch, apiUrl } from "./api";
import { ApiError } from "./asset-api";

export const ASSET_ISSUE_STATUSES = ["open", "in_progress", "resolved"] as const;
export type AssetIssueStatus = (typeof ASSET_ISSUE_STATUSES)[number];
export type AssetIssueVisibility = "shared" | "internal";

export type AssetIssueMessage = {
  id: string;
  body: string;
  visibility: AssetIssueVisibility;
  authorType: string;
  authorName: string;
  createdAt: string;
};

export type AssetIssueAttachment = {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  url: string;
  createdAt: string;
};

export type AssetIssueSummary = {
  id: string;
  status: AssetIssueStatus;
  description: string;
  reporter: { type: string; name: string };
  unit: { id: string; serialNumber: string | null; lifecycle: string };
  property: {
    id: string;
    name: string;
    category: string;
    imageUrl: string | null;
  };
  employee: { id: string; name: string } | null;
  attachmentCount: number;
  messageCount: number;
  resolvedAt: string | null;
  lastActivityAt: string;
  createdAt: string;
  updatedAt: string;
};

export type AssetIssueDetail = AssetIssueSummary & {
  messages: AssetIssueMessage[];
  attachments: AssetIssueAttachment[];
};

export type AssetIssueNotification = {
  id: string;
  issueId: string;
  title?: string;
  message?: string;
  body?: string;
  read?: boolean;
  readAt?: string | null;
  type?: string;
  entityType?: string;
  targetUrl?: string;
  createdAt: string;
};

type AssetIssueSummaryResponse = Omit<AssetIssueSummary, "reporter" | "employee"> & {
  reporter?: AssetIssueSummary["reporter"];
  employee?: AssetIssueSummary["employee"];
  reporterType?: string;
  reporterName?: string;
  currentEmployee?: AssetIssueSummary["employee"];
};

type AssetIssueNotificationResponse = AssetIssueNotification & {
  entityId?: string;
  targetUrl?: string;
};

export type AssetIssuesPage = {
  items: AssetIssueSummary[];
  total: number;
  counts: { open: number; inProgress: number; resolved: number };
};

export function normalizeAssetIssueSummary(item: AssetIssueSummaryResponse): AssetIssueSummary {
  return {
    ...item,
    reporter: item.reporter ?? {
      type: item.reporterType ?? "user",
      name: item.reporterName ?? "Unknown",
    },
    employee: item.employee ?? item.currentEmployee ?? null,
  };
}

function normalizeNotification(item: AssetIssueNotificationResponse): AssetIssueNotification {
  return {
    ...item,
    issueId: item.issueId ?? item.entityId ?? item.targetUrl?.split("/").pop() ?? "",
    read: item.read ?? Boolean(item.readAt),
  };
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await apiFetch(apiUrl(path), {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    ...init,
  });
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const message =
      data && typeof data === "object" && "error" in data
        ? String((data as { error: unknown }).error)
        : `Request failed (${res.status})`;
    throw new ApiError(message, res.status);
  }
  return data as T;
}

export async function listAssetIssues(params: {
  status?: AssetIssueStatus;
  q?: string;
  employeeId?: string;
  unitId?: string;
  limit: number;
  offset: number;
  sort?: "asset" | "status" | "activity";
  dir?: "asc" | "desc";
}): Promise<AssetIssuesPage> {
  const search = new URLSearchParams({
    limit: String(params.limit),
    offset: String(params.offset),
  });
  if (params.status) search.set("status", params.status);
  if (params.q) search.set("q", params.q);
  if (params.employeeId) search.set("employeeId", params.employeeId);
  if (params.sort) search.set("sort", params.sort);
  if (params.dir) search.set("dir", params.dir);
  if (params.unitId) search.set("unitId", params.unitId);
  const data = await request<{
    items: AssetIssueSummaryResponse[];
    total: number;
    counts: AssetIssuesPage["counts"];
  }>(`/api/asset-issues?${search.toString()}`);
  return { ...data, items: data.items.map(normalizeAssetIssueSummary) };
}

export async function createAssetIssue(unitId: string, description: string) {
  const data = await request<{ item: AssetIssueSummaryResponse }>("/api/asset-issues", {
    method: "POST",
    body: JSON.stringify({ unitId, description }),
  });
  return normalizeAssetIssueSummary(data.item);
}

export async function getAssetIssue(id: string) {
  const data = await request<{
    item: AssetIssueSummaryResponse & Pick<AssetIssueDetail, "messages" | "attachments">;
  }>(`/api/asset-issues/${id}`);
  return normalizeAssetIssueSummary(data.item) as AssetIssueDetail;
}

export async function addAssetIssueMessage(
  id: string,
  body: string,
  visibility: AssetIssueVisibility,
) {
  const data = await request<{ item: AssetIssueMessage }>(`/api/asset-issues/${id}/messages`, {
    method: "POST",
    body: JSON.stringify({ body, visibility }),
  });
  return data.item;
}

export async function updateAssetIssueStatus(id: string, status: AssetIssueStatus) {
  const data = await request<{ item: AssetIssueSummaryResponse }>(
    `/api/asset-issues/${id}/status`,
    { method: "PATCH", body: JSON.stringify({ status }) },
  );
  return normalizeAssetIssueSummary(data.item);
}

export async function uploadAssetIssueAttachment(id: string, file: File) {
  const form = new FormData();
  form.append("file", file);
  const res = await apiFetch(apiUrl(`/api/asset-issues/${id}/attachments`), {
    method: "POST",
    credentials: "include",
    body: form,
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    throw new ApiError(
      data && typeof data === "object" && "error" in data
        ? String((data as { error: unknown }).error)
        : `Upload failed (${res.status})`,
      res.status,
    );
  }
  return (data as { item: AssetIssueAttachment }).item;
}

export function assetIssueAttachmentUrl(issueId: string, attachmentId: string) {
  return apiUrl(`/api/asset-issues/${issueId}/attachments/${attachmentId}/download`);
}

export async function listAssetIssueNotifications() {
  const data = await request<{
    items: AssetIssueNotificationResponse[];
    unread?: number;
  }>("/api/asset-issues/notifications");
  const items = data.items
    .map(normalizeNotification)
    .filter((item) => item.issueId && (!item.entityType || item.entityType === "asset_issue"));
  return {
    items,
    unread: data.unread ?? items.filter((item) => !item.readAt).length,
  };
}

export function markAssetIssueNotificationRead(id: string) {
  return request<void>(`/api/asset-issues/notifications/${id}/read`, {
    method: "PATCH",
  });
}
